import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Wrench,
  Loader2,
  CheckCircle,
  DollarSign,
  Clock,
  Package,
  Plus,
  Trash2,
  FileText,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContractorQuotePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [quoteRequest, setQuoteRequest] = useState(null);
  
  // Quote form state
  const [quoteType, setQuoteType] = useState("single_price");
  const [laborRate, setLaborRate] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [parts, setParts] = useState([]);
  const [totalPrice, setTotalPrice] = useState("");
  const [description, setDescription] = useState("");
  const [leadTimeDays, setLeadTimeDays] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchQuoteRequest();
  }, [token]);

  const fetchQuoteRequest = async () => {
    try {
      const res = await fetch(`${API}/contractor/quote/${token}`);
      if (res.ok) {
        const data = await res.json();
        setQuoteRequest(data);
      } else {
        const err = await res.json();
        setError(err.detail || "Invalid or expired quote link");
      }
    } catch (err) {
      setError("Failed to load quote request");
    } finally {
      setLoading(false);
    }
  };

  const addPart = () => {
    setParts([...parts, { name: "", part_number: "", quantity: 1, unit_price: "" }]);
  };

  const updatePart = (index, field, value) => {
    const newParts = [...parts];
    newParts[index][field] = field === "quantity" ? parseInt(value) || 1 : value;
    setParts(newParts);
  };

  const removePart = (index) => {
    setParts(parts.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    if (quoteType === "single_price") {
      return parseFloat(totalPrice) || 0;
    } else if (quoteType === "labor_only") {
      return (parseFloat(laborRate) || 0) * (parseFloat(estimatedHours) || 0);
    } else {
      const laborTotal = (parseFloat(laborRate) || 0) * (parseFloat(estimatedHours) || 0);
      const partsTotal = parts.reduce((sum, p) => sum + (parseFloat(p.unit_price) || 0) * (p.quantity || 1), 0);
      return laborTotal + partsTotal;
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (quoteType === "labor_only" && (!laborRate || !estimatedHours)) {
      toast.error("Please enter labor rate and estimated hours");
      return;
    }
    if (quoteType === "single_price" && !totalPrice) {
      toast.error("Please enter total price");
      return;
    }
    if (quoteType === "parts_and_labor" && (!laborRate || !estimatedHours)) {
      toast.error("Please enter labor rate and estimated hours");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        quote_type: quoteType,
        labor_rate: parseFloat(laborRate) || null,
        estimated_hours: parseFloat(estimatedHours) || null,
        parts: quoteType === "parts_and_labor" ? parts.map(p => ({
          name: p.name,
          part_number: p.part_number || null,
          quantity: p.quantity || 1,
          unit_price: parseFloat(p.unit_price) || 0
        })) : null,
        total_price: quoteType === "single_price" ? parseFloat(totalPrice) : null,
        description: description || null,
        lead_time_days: parseInt(leadTimeDays) || null,
        notes: notes || null
      };

      const res = await fetch(`${API}/contractor/quote/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success("Quote submitted successfully!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit quote");
      }
    } catch (err) {
      toast.error("Failed to submit quote");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Quote Link Error</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Quote Submitted!</h2>
            <p className="text-slate-600">
              Thank you for submitting your quote. SterileMate will review it and contact you if selected.
            </p>
            <p className="text-2xl font-bold text-emerald-600 mt-4">
              ${calculateTotal().toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-emerald-600 mb-2">Ask Isaac</h1>
          <p className="text-slate-600">Contractor Quote Submission</p>
        </div>

        {/* Work Order Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-emerald-600" />
              Work Order Details
            </CardTitle>
            <CardDescription>
              Review the work order details before submitting your quote
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Work Order ID</p>
                <p className="font-medium">{quoteRequest?.work_order?.id}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Priority</p>
                <p className="font-medium capitalize">{quoteRequest?.work_order?.priority || "Normal"}</p>
              </div>
            </div>
            {quoteRequest?.request?.facility && (
              <div>
                <p className="text-sm text-slate-500">Facility</p>
                <p className="font-medium">{quoteRequest.request.facility}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500">Scope of Work</p>
              <p className="font-medium">{quoteRequest?.work_order?.scope_of_work}</p>
            </div>
            {quoteRequest?.work_order?.special_instructions && (
              <div>
                <p className="text-sm text-slate-500">Special Instructions</p>
                <p className="font-medium">{quoteRequest.work_order.special_instructions}</p>
              </div>
            )}
            {quoteRequest?.work_order?.parts_required?.length > 0 && (
              <div>
                <p className="text-sm text-slate-500">Parts Required</p>
                <ul className="list-disc list-inside">
                  {quoteRequest.work_order.parts_required.map((p, i) => (
                    <li key={i} className="font-medium">{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quote Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Submit Your Quote
            </CardTitle>
            <CardDescription>
              Choose how you want to price this job
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quote Type Selection */}
            <div>
              <Label className="text-base font-medium mb-3 block">Quote Type</Label>
              <RadioGroup value={quoteType} onValueChange={setQuoteType} className="space-y-3">
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <RadioGroupItem value="labor_only" id="labor_only" className="mt-1" />
                  <div>
                    <p className="font-medium">Labor Only</p>
                    <p className="text-sm text-slate-500">Charge by hourly rate × estimated hours</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <RadioGroupItem value="parts_and_labor" id="parts_and_labor" className="mt-1" />
                  <div>
                    <p className="font-medium">Parts + Labor (Itemized)</p>
                    <p className="text-sm text-slate-500">List parts separately with labor charge</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <RadioGroupItem value="single_price" id="single_price" className="mt-1" />
                  <div>
                    <p className="font-medium">Single Price</p>
                    <p className="text-sm text-slate-500">One total price for the entire job</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Labor Fields (for labor_only and parts_and_labor) */}
            {(quoteType === "labor_only" || quoteType === "parts_and_labor") && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Labor Rate ($/hour) *</Label>
                  <Input
                    type="number"
                    value={laborRate}
                    onChange={(e) => setLaborRate(e.target.value)}
                    placeholder="85.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <Label>Estimated Hours *</Label>
                  <Input
                    type="number"
                    value={estimatedHours}
                    onChange={(e) => setEstimatedHours(e.target.value)}
                    placeholder="4"
                    min="0"
                    step="0.5"
                  />
                </div>
                {laborRate && estimatedHours && (
                  <div className="col-span-2 text-right">
                    <span className="text-sm text-slate-600">Labor Total: </span>
                    <span className="font-bold text-emerald-600">
                      ${((parseFloat(laborRate) || 0) * (parseFloat(estimatedHours) || 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Parts List (for parts_and_labor) */}
            {quoteType === "parts_and_labor" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Parts List</Label>
                  <Button size="sm" variant="outline" onClick={addPart}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Part
                  </Button>
                </div>
                {parts.map((part, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg items-end">
                    <div className="col-span-4">
                      <Label className="text-xs">Part Name</Label>
                      <Input
                        value={part.name}
                        onChange={(e) => updatePart(index, "name", e.target.value)}
                        placeholder="Pipe fitting"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Part #</Label>
                      <Input
                        value={part.part_number}
                        onChange={(e) => updatePart(index, "part_number", e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        value={part.quantity}
                        onChange={(e) => updatePart(index, "quantity", e.target.value)}
                        min="1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Unit $</Label>
                      <Input
                        type="number"
                        value={part.unit_price}
                        onChange={(e) => updatePart(index, "unit_price", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removePart(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {parts.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No parts added. Click "Add Part" to include materials.
                  </p>
                )}
              </div>
            )}

            {/* Single Price Field */}
            {quoteType === "single_price" && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Total Price *</Label>
                  <Input
                    type="number"
                    value={totalPrice}
                    onChange={(e) => setTotalPrice(e.target.value)}
                    placeholder="450.00"
                    min="0"
                    step="0.01"
                    className="text-lg"
                  />
                </div>
                <div>
                  <Label>Description of Work</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Complete repair including labor and all materials..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Lead Time & Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Lead Time (days)
                </Label>
                <Input
                  type="number"
                  value={leadTimeDays}
                  onChange={(e) => setLeadTimeDays(e.target.value)}
                  placeholder="1"
                  min="0"
                />
              </div>
              <div className="col-span-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information..."
                  rows={2}
                />
              </div>
            </div>

            {/* Total Summary */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <span className="text-lg font-medium text-emerald-800">Your Quote Total:</span>
                <span className="text-2xl font-bold text-emerald-600">
                  ${calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              size="lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Submit Quote
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          © 2026 AskIsaac Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
